import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import DriverTicketCard from "@/components/DriverTicketCard";
import InstallAppButton from "@/components/InstallAppButton";
import DriverLocationBeacon from "@/components/DriverLocationBeacon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { LogOut, RefreshCw, Route, Map as MapIcon, KeyRound, Droplet, Package, Receipt } from "lucide-react";
import { toast } from "sonner";
import RouteMapDialog from "@/components/RouteMapDialog";
import LocationGate from "@/components/LocationGate";
import { useStayPut } from "@/lib/useStayPut";
import { extractZip, buildRouteOrder, nearestTicketMiles, isProductsOnlyTicket, UNROUTED_SORT_INDEX } from "@/lib/routeUtils";

// --- Small label helper (replace nested ternary) ---
const getRouteButtonLabel = (driverLoc, locating, stops) => {
  if (driverLoc) return `Start route from my location (${stops} stop${stops === 1 ? "" : "s"})`;
  if (locating) return "Getting your GPS…";
  return "Tap to share location, then start route";
};

export default function DriverDay() {
  useStayPut();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "secretary";
  const [tickets, setTickets] = useState([]);
  const [settings, setSettings] = useState({ price_per_gallon: 5 });
  const [sortMode, setSortMode] = useState("route"); // route (default) | date | zip
  const [dayMode, setDayMode] = useState("today"); // today | tomorrow
  const [typeMode, setTypeMode] = useState("chlorine"); // chlorine | products
  const [search, setSearch] = useState("");
  // Drivers default to "only my assignments". Admins/secretaries see everything.
  const [mineOnly, setMineOnly] = useState(user?.role === "driver");
  const [driverLoc, setDriverLoc] = useState(null);
  const [locating, setLocating] = useState(false);
  // The page is unusable until the browser grants location permission.
  const [geoAllowed, setGeoAllowed] = useState(false);
  const onLocationGranted = useCallback((coords) => {
    setGeoAllowed(true);
    setDriverLoc(coords);
  }, []);
  const [geocoding, setGeocoding] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/tickets?status_filter=open").then((r) => setTickets(r.data));
    // Settings include the global "enforce_driver_assignment" flag. Drivers
    // can read /settings too — when the admin turns enforcement OFF, we hide
    // the driver's local "Only my tickets" toggle so they automatically see
    // every ticket again.
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Once we know the global setting, keep the driver's local "mine only"
  // toggle in sync: enforcement OFF should never hide tickets from a driver.
  const enforceAssignment = settings.enforce_driver_assignment !== false;
  useEffect(() => {
    if (!enforceAssignment && mineOnly) setMineOnly(false);
  }, [enforceAssignment, mineOnly]);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error("This device doesn't support GPS.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDriverLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Got your location");
        setLocating(false);
      },
      (err) => {
        toast.error(err?.message || "Couldn't get location. Enable location access for this site.");
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const onSortChange = (v) => {
    setSortMode(v);
    if (v === "route" && !driverLoc && !locating) requestLocation();
  };

  const geocodeMissing = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post("/pools/geocode-missing");
      toast.success(`Geocoded ${data.geocoded} pool address${data.geocoded === 1 ? "" : "es"}. Reloading…`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setGeocoding(false);
    }
  };

  const ticketDistance = (t) => nearestTicketMiles(driverLoc, t);

  const buildRoute = useCallback((arr) => buildRouteOrder(arr, driverLoc), [driverLoc]);

  const dayTickets = useMemo(() => {
    let arr = tickets.slice();
    // Today (default) shows today + overdue; Tomorrow shows only tomorrow's tickets
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const tmrw = new Date(today);
    tmrw.setDate(tmrw.getDate() + 1);
    const tmrwStr = tmrw.toISOString().slice(0, 10);
    if (dayMode === "today") {
      arr = arr.filter((t) => !t.delivery_date || t.delivery_date <= todayStr);
    } else {
      arr = arr.filter((t) => t.delivery_date === tmrwStr);
    }
    if (mineOnly && user?.id) {
      // Strict view: only tickets explicitly assigned to me. Unassigned ones
      // hide here so a driver can focus on their own route.
      arr = arr.filter((t) => t.assigned_driver_id === user.id);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((t) =>
        (t.customer_name || "").toLowerCase().includes(q) ||
        (t.business_name || "").toLowerCase().includes(q) ||
        (t.pool_deliveries || []).some((d) => (d.pool_address || "").toLowerCase().includes(q))
      );
    }
    return arr;
  }, [tickets, search, dayMode, mineOnly, user?.id]);

  const typeCounts = useMemo(() => ({
    chlorine: dayTickets.filter((t) => !isProductsOnlyTicket(t)).length,
    products: dayTickets.filter(isProductsOnlyTicket).length,
  }), [dayTickets]);

  const filteredSorted = useMemo(() => {
    const arr = dayTickets.filter((t) => (typeMode === "products" ? isProductsOnlyTicket(t) : !isProductsOnlyTicket(t)));
    if (sortMode === "zip") {
      arr.sort((a, b) => {
        const za = extractZip(a.pool_deliveries?.[0]?.pool_address) || "99999";
        const zb = extractZip(b.pool_deliveries?.[0]?.pool_address) || "99999";
        return za.localeCompare(zb);
      });
    } else if (sortMode === "date") {
      // Today first (ascending), latest at the bottom; missing dates pushed to end
      arr.sort((a, b) => {
        const ad = a.delivery_date || "9999-12-31";
        const bd = b.delivery_date || "9999-12-31";
        return ad.localeCompare(bd);
      });
    } else if (sortMode === "route") {
      // Nearest-neighbor optimized route from driver GPS
      const order = buildRoute(arr);
      const idx = new Map(order.map((id, i) => [id, i]));
      arr.sort((a, b) => (idx.get(a.id) ?? UNROUTED_SORT_INDEX) - (idx.get(b.id) ?? UNROUTED_SORT_INDEX));
    }
    return arr;
  }, [dayTickets, typeMode, sortMode, buildRoute]);

  const todayCount = filteredSorted.length;
  const hiddenFuture = tickets.length - dayTickets.length;
  const ungeocodedCount = filteredSorted.reduce((n, t) => n + (t.pool_deliveries || []).filter((d) => d.latitude == null).length, 0);

  return (
    <div className="min-h-screen bg-surface no-print">
      <DriverLocationBeacon />
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/90 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="overline">Driver Day</div>
            <h1 className="font-display text-xl font-black tracking-tighter leading-tight" data-testid="driver-title">
              {todayCount} ticket{todayCount === 1 ? "" : "s"} {dayMode === "today" ? "for today" : "for tomorrow"}
            </h1>
            {dayMode === "today" && hiddenFuture > 0 && (
              <div className="text-[10px] text-muted-foreground font-mono mt-0.5" data-testid="driver-future-hidden">
                {hiddenFuture} future ticket{hiddenFuture === 1 ? "" : "s"} hidden
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => navigate("/admin")} className="rounded-sm text-xs" data-testid="driver-back">
                Office
              </Button>
            )}
            <InstallAppButton />
            <Button size="sm" variant="outline" onClick={() => navigate("/driver/expenses")} className="rounded-sm text-xs" data-testid="driver-my-expenses" title="Submit gas receipts & other expenses">
              <Receipt className="w-4 h-4 mr-1" /> Expenses
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="driver-account-button" title="Account">
                  <KeyRound className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-display">Account</DialogTitle>
                  <DialogDescription>Signed in as {user?.email}</DialogDescription>
                </DialogHeader>
                <ChangePasswordCard compact testId="driver-change-password" />
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="outline" onClick={async () => { await logout(); navigate("/login"); }} className="rounded-sm" data-testid="driver-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {(geoAllowed || isAdmin) && (
        <div className="max-w-2xl mx-auto px-4 pb-3 space-y-2">
          <Tabs value={dayMode} onValueChange={setDayMode}>
            <TabsList className="rounded-sm w-full">
              <TabsTrigger value="today" className="flex-1" data-testid="driver-day-today">Today</TabsTrigger>
              <TabsTrigger value="tomorrow" className="flex-1" data-testid="driver-day-tomorrow">Tomorrow</TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={typeMode} onValueChange={setTypeMode}>
            <TabsList className="rounded-sm w-full">
              <TabsTrigger value="chlorine" className="flex-1" data-testid="driver-type-chlorine">
                <Droplet className="w-3.5 h-3.5 mr-1" /> Chlorine ({typeCounts.chlorine})
              </TabsTrigger>
              <TabsTrigger value="products" className="flex-1" data-testid="driver-type-products">
                <Package className="w-3.5 h-3.5 mr-1" /> Products ({typeCounts.products})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Input className="rounded-sm h-11" placeholder="Search customer or address…" value={search}
                 onChange={(e) => setSearch(e.target.value)} data-testid="driver-search" />
          {user?.role === "driver" && enforceAssignment && (
            <label className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2 cursor-pointer bg-secondary/30" data-testid="driver-mine-only-label">
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">Only show my tickets</div>
                <p className="text-[10px] text-muted-foreground font-mono">Off = also show unassigned work the office may still be triaging.</p>
              </div>
              <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="driver-mine-only-toggle" />
            </label>
          )}
          <Tabs value={sortMode} onValueChange={onSortChange}>
            <TabsList className="rounded-sm w-full">
              <TabsTrigger value="date" className="flex-1" data-testid="driver-sort-date">By date</TabsTrigger>
              <TabsTrigger value="zip" className="flex-1" data-testid="driver-sort-zip">By zip</TabsTrigger>
              <TabsTrigger value="route" className="flex-1" data-testid="driver-sort-route">
                <Route className="w-3.5 h-3.5 mr-1" /> Best route
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            className="rounded-sm w-full h-9 text-xs"
            onClick={() => setMapOpen(true)}
            data-testid="driver-view-map"
          >
            <MapIcon className="w-3.5 h-3.5 mr-2" />
            {sortMode === "route" ? "View best route on map" : "View all stops on map"}
          </Button>
          {sortMode === "route" && (
            <div className="flex items-center justify-between text-xs px-1 pt-1">
              <div className="text-muted-foreground">
                {(() => {
                  if (locating) return "Getting GPS…";
                  if (driverLoc) return (
                    <>Optimized from <span className="mono-num">{driverLoc.lat.toFixed(3)}, {driverLoc.lng.toFixed(3)}</span></>
                  );
                  return "GPS needed — tap Refresh";
                })()}
              </div>
              <div className="flex items-center gap-2">
                {ungeocodedCount > 0 && (
                  <button onClick={geocodeMissing} disabled={geocoding} className="text-primary hover:underline disabled:opacity-50" data-testid="driver-geocode-missing">
                    {geocoding ? "Geocoding…" : `Geocode ${ungeocodedCount} address${ungeocodedCount === 1 ? "" : "es"}`}
                  </button>
                )}
                <button onClick={requestLocation} className="text-primary hover:underline inline-flex items-center gap-1" data-testid="driver-refresh-location">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </header>

      {/* GPS gate is for drivers only — admins/secretaries can view without sharing location */}
      {!geoAllowed && !isAdmin ? (
        <LocationGate onGranted={onLocationGranted} />
      ) : (
      <>
      {/* Cards */}
      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {filteredSorted.length > 0 && (
          <a
            href={(() => {
              if (!driverLoc) return "#"; // route must start from driver — link is blocked until we have GPS
              const stops = filteredSorted
                .map((t) => {
                  const d = (t.pool_deliveries || []).find((p) => p.pool_address);
                  if (d?.latitude != null && d?.longitude != null) return `${d.latitude},${d.longitude}`;
                  if (d?.pool_address) return d.pool_address;
                  // Products-only / no-pool tickets: route to the customer's address
                  if (t.customer_latitude != null && t.customer_longitude != null) return `${t.customer_latitude},${t.customer_longitude}`;
                  return t.customer_address || null;
                })
                .filter(Boolean)
                .slice(0, 9); // Google Maps caps at ~9 waypoints
              if (stops.length === 0) return "#";
              const destination = stops.pop();
              const waypoints = stops.length ? `&waypoints=${stops.map(encodeURIComponent).join("|")}` : "";
              const origin = `${driverLoc.lat},${driverLoc.lng}`;
              return `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints}`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (!driverLoc) {
                e.preventDefault();
                if (locating) {
                  toast.message("Getting your GPS — try again in a second.");
                } else {
                  toast.message("Sharing your location so the route starts from where you are.");
                  requestLocation();
                }
              }
            }}
            aria-disabled={!driverLoc}
            className={`block w-full text-center font-semibold text-sm tracking-wide rounded-sm h-12 leading-[3rem] ${driverLoc ? "bg-foreground text-background hover:bg-foreground/90" : "bg-foreground/40 text-background cursor-pointer"}`}
            data-testid="driver-start-route-button"
          >
            <MapIcon className="w-4 h-4 inline-block mr-2 -mt-0.5" />
            {getRouteButtonLabel(driverLoc, locating, filteredSorted.length)}
          </a>
        )}
        {filteredSorted.length === 0 && (
          <div className="border border-border rounded-sm p-8 text-center text-sm text-muted-foreground" data-testid="driver-empty">
            {(() => {
              if (search) return "No matches.";
              const other = typeMode === "chlorine" ? typeCounts.products : typeCounts.chlorine;
              if (other > 0) return typeMode === "chlorine" ? "No chlorine deliveries — check the Products tab." : "No product drop-offs — check the Chlorine tab.";
              if (dayMode === "tomorrow") return "Nothing scheduled for tomorrow yet.";
              if (tickets.length > 0) return "Nothing scheduled for today — check Tomorrow.";
              return "No open tickets — nice work.";
            })()}
          </div>
        )}
        {filteredSorted.map((t, i) => {
          const zip = extractZip(t.pool_deliveries?.[0]?.pool_address || t.customer_address);
          const dist = sortMode === "route" ? ticketDistance(t) : null;
          const routeIdx = sortMode === "route" ? i : null;
          const next = filteredSorted[i + 1];
          const nextStopInfo = next ? { nextTicketId: next.id } : null;
          return (
            <div key={t.id} className="space-y-1">
              {sortMode === "zip" && zip && (
                <div className="text-[10px] font-mono text-muted-foreground px-1">ZIP {zip}</div>
              )}
              <DriverTicketCard t={t} settings={settings} onClose={load} distanceMiles={dist} routeIndex={routeIdx} nextStopInfo={nextStopInfo} showPrices={isAdmin} />
            </div>
          );
        })}
        <div className="text-xs text-muted-foreground text-center pt-4 pb-8">
          Signed in as <span className="font-mono">{user?.email}</span>
        </div>
      </main>
      <RouteMapDialog
        open={mapOpen}
        onOpenChange={setMapOpen}
        tickets={tickets}
        sortedTickets={filteredSorted}
        sortMode={sortMode}
        driverLoc={driverLoc}
      />
      </>
      )}
    </div>
  );
}
